import { Stack } from 'expo-router';
import type { ReactNode } from 'react';
import Animated, { useAnimatedScrollHandler } from 'react-native-reanimated';
import { StyleSheet } from 'react-native-unistyles';
import Avatar from '@/components/Custom/Avatar';
import * as Header from '@/components/Custom/Header';
import ModalKeyboardAwareScrollView from '@/components/Custom/ModalKeyboardAwareScrollView';
import Text from '@/components/UI/Text';
import PersonProfileMasthead, {
  PERSON_PROFILE_MASTHEAD_MIN_HEIGHT,
  usePersonProfileHeaderAnimation,
} from './PersonProfileMasthead';

export default function PersonEditorScrollView({
  children,
  actionContent,
  masthead,
  name,
  avatarId,
  color,
  showCompactHeader = true,
}: {
  children: ReactNode;
  actionContent?: ReactNode;
  masthead: ReactNode;
  name: string;
  avatarId?: string | null;
  color?: string | null;
  showCompactHeader?: boolean;
}) {
  const mastheadMinHeight = actionContent
    ? PERSON_PROFILE_MASTHEAD_MIN_HEIGHT
    : 256;
  const { scrollY, compactHeaderAnimatedProps, compactHeaderAnimatedStyle } =
    usePersonProfileHeaderAnimation();
  const onScroll = useAnimatedScrollHandler((event) => {
    scrollY.set(event.contentOffset.y);
  });
  return (
    <>
      {showCompactHeader && (
        <Stack.Screen
          options={{
            headerBackTitle: '',
            headerBackButtonDisplayMode: 'minimal',
            headerTitle: () => (
              <Animated.View
                animatedProps={compactHeaderAnimatedProps}
                style={compactHeaderAnimatedStyle}
              >
                <Header.MastheadTitleContainer>
                  <Avatar
                    id={avatarId ?? ''}
                    color={color}
                    name={name}
                    size="md"
                  />
                  <Text size="xl" numberOfLines={1} flexShrink={1}>
                    {name}
                  </Text>
                </Header.MastheadTitleContainer>
              </Animated.View>
            ),
          }}
        />
      )}
      <ModalKeyboardAwareScrollView
        onScroll={onScroll}
        scrollEventThrottle={16}
        contentContainerStyle={styles.content}
      >
        <PersonProfileMasthead
          actionContent={actionContent}
          minHeight={mastheadMinHeight}
          name={name}
          avatarId={avatarId}
          color={color}
          profileLabel=""
          scrollY={scrollY}
          surfaceStyle={styles.surface}
          testID="PersonEditorMasthead"
        >
          {masthead}
        </PersonProfileMasthead>
        {children}
      </ModalKeyboardAwareScrollView>
    </>
  );
}
const styles = StyleSheet.create((theme) => ({
  content: { flexGrow: 1 },
  surface: {
    backgroundColor: theme.colors.surface,
    paddingVertical: theme.space('xl'),
    justifyContent: 'center',
  },
}));
